import { useEffect, useMemo, useState } from 'react';
import { procurementApi, type PurchaseRequestResponse } from '@/api/procurement.api';
import type { SelectOption } from '@/components/ui/Select';
import { formatPurchaseRequestLabel } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

export function usePurchaseRequestOptions(pageSize = 200) {
  const [rows, setRows] = useState<PurchaseRequestResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    procurementApi
      .listPurchaseRequests({ page: 0, size: pageSize })
      .then((res) => setRows(res.content))
      .catch((err) => showApiError(err, 'Failed to load purchase requests'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () => rows.map((r) => ({ value: String(r.id), label: formatPurchaseRequestLabel(r) })),
    [rows],
  );

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const getPurchaseRequest = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined) => {
    const row = getPurchaseRequest(id);
    if (!row) return id != null && id !== '' ? `PR #${id}` : '—';
    return formatPurchaseRequestLabel(row);
  };

  return { rows, options, loading, getPurchaseRequest, getLabel, byId };
}
